import { PIM_WORKFLOW_STATE, type PimWorkflowReceipt, type PimWorkflowScope } from "@nova-ai/shared"
import {
  Check,
  Circle,
  CircleAlert,
  Clock,
  HelpCircle,
  Minus,
  Pause,
  RefreshCw,
} from "lucide-react"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime } from "../../lib/format-date"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { receiptOutcome, workflowReason } from "./workflow-history-presentation"

type Counts = PimWorkflowReceipt["summary"]["generation"]
const countLabels = {
  complete: "complete",
  failed: "failed",
  pending: "pending",
  unknown: "unknown",
  notApplicable: "not applicable",
  superseded: "superseded",
} as const
const stageStyles = {
  queued: { tone: "info", icon: Clock },
  running: { tone: "info", icon: Circle },
  retrying: { tone: "warning", icon: RefreshCw },
  failed: { tone: "destructive", icon: CircleAlert },
  complete: { tone: "success", icon: Check },
  not_applicable: { tone: "muted", icon: Minus },
  blocked: { tone: "warning", icon: Pause },
  superseded: { tone: "muted", icon: Minus },
  unknown: { tone: "muted", icon: HelpCircle },
} satisfies Record<PimWorkflowScope["generation"]["state"], { tone: BadgeTone; icon: typeof Check }>

export function WorkflowCounts({ counts }: { counts: Counts }) {
  const entries = (
    ["complete", "failed", "pending", "unknown", "notApplicable", "superseded"] as const
  ).filter((key) => counts[key] > 0)
  return (
    <div className="space-y-1 text-xs tabular-nums">
      {entries.length === 0
        ? "No target evidence"
        : entries.map((key) => <p key={key}>{`${counts[key]} ${countLabels[key]}`}</p>)}
    </div>
  )
}

export function WorkflowStage({
  stage,
  showTime = true,
}: {
  stage: PimWorkflowScope["generation"]
  showTime?: boolean
}) {
  const { tone, icon: Icon } = stageStyles[stage.state]
  const isPaused = stage.state === PIM_WORKFLOW_STATE.blocked && stage.reason?.includes("paused")
  const label = isPaused ? "Paused" : humanizeEnumLabel(stage.state)
  return (
    <div className="min-w-0 space-y-2">
      <StatusBadge tone={tone}>
        <Icon aria-hidden="true" className="size-3.5" />

        {label}
      </StatusBadge>

      {(Boolean(stage.reason) || stage.state === PIM_WORKFLOW_STATE.failed) && (
        <p className="break-words text-xs">{workflowReason(stage.reason)}</p>
      )}

      {stage.evidence === "expired" && (
        <p className="text-xs text-muted-foreground">Expired evidence</p>
      )}

      {showTime && stage.recordedAt && (
        <time dateTime={stage.recordedAt} className="block text-xs text-muted-foreground">
          {formatDateTime(stage.recordedAt)}
        </time>
      )}
    </div>
  )
}

export function WorkflowReceiptSummary({ receipt }: { receipt: PimWorkflowReceipt }) {
  const outcome = receiptOutcome(receipt)
  return (
    <div className="space-y-2">
      <StatusBadge tone={outcome.tone}>
        {outcome.tone === "destructive" ? (
          <CircleAlert aria-hidden="true" className="size-4" />
        ) : (
          <Circle aria-hidden="true" className="size-4" />
        )}

        {outcome.label}
      </StatusBadge>

      {receipt.deliveryResolution && (
        <div className="space-y-1">
          <StatusBadge tone="success">Present image obligation resolved later</StatusBadge>

          <p className="text-xs text-muted-foreground">
            {`Delivery #${receipt.deliveryResolution.deliveryId} · ${formatDateTime(receipt.deliveryResolution.resolvedAt)}`}
          </p>

          <p className="break-all font-mono text-xs text-muted-foreground">
            {`Resolution ${receipt.deliveryResolution.id}`}
          </p>

          <p className="text-xs text-muted-foreground">
            Original processing failed. Historical steps remain unknown; this proof covers saved
            image identities.
          </p>
        </div>
      )}

      {receipt.validation === "rejected" && (
        <p className="text-sm text-destructive">{workflowReason(receipt.validationReason)}</p>
      )}
    </div>
  )
}
